import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useBanks from '@/hooks/useBanks';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import LabeledInput, { SearchableSelect } from '@/pages/newSupported/components/LabeledInput';
import Button from '@/components/ui/Button';
import { ManagementSupportedDtoIn } from '@/models/in/ManagementSupportedDtoIn';
import { BankAccountDtoIn } from '@/models/in/BankAccountDtoIn';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import { useGetAuthApi, usePostAuthApi, useDeleteAuthApi } from '@/hooks/UseApiCalls';
import { putAuth } from '@/api/ApiCalls';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import addIcon from '@/assets/icons/add.svg';
import minusIcon from '@/assets/icons/minus.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import styles from './EditBankAccountDialog.module.css';
import { DocumentDtoIn } from '@/models/in/DocumentDtoIn';

interface EditBankAccountDialogProps {
  open: boolean;
  onClose: () => void;
  supported: ManagementSupportedDtoIn;
  onSuccess: () => void;
}

interface EditableBankAccount {
  id: number | null; // null for new accounts
  accountOwnerName: string;
  bankNumber: string;
  branchNumber: string;
  accountNumber: string;
  isModified: boolean;
}

interface BankAccountPayload {
  type: PaymentMethodType;
  bankNumber: number;
  branchNumber: string;
  accountNumber: string;
  accountOwnerName: string;
  documents: DocumentDtoIn[];
}

const EditBankAccountDialog = ({ open, onClose, supported, onSuccess }: EditBankAccountDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [accounts, setAccounts] = useState<EditableBankAccount[]>([]);
  const [originalAccounts, setOriginalAccounts] = useState<EditableBankAccount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<EditableBankAccount | null>(null);

  const { data: paymentInfos, isLoading } = useGetAuthApi<BankAccountDtoIn[]>(open ? `management/supporteds/${supported.id}/payment-infos` : '');

  const { data: linkedRequestsData } = useGetAuthApi<{ count: number }>(
    accountToDelete?.id ? `management/supporteds/${supported.id}/payment-infos/${accountToDelete.id}/linked-support-requests-count` : ''
  );

  const { mutateAsync: createPaymentInfo } = usePostAuthApi<unknown, BankAccountPayload>(`management/supporteds/${supported.id}/payment-infos`);

  const { mutate: deletePaymentInfo, isPending: isDeleting } = useDeleteAuthApi<void>(
    accountToDelete?.id ? `management/supporteds/${supported.id}/payment-infos/${accountToDelete.id}` : ''
  );

  // Initialize accounts when dialog opens or payment infos load
  useEffect(() => {
    if (open && paymentInfos) {
      const initialAccounts: EditableBankAccount[] = paymentInfos
        .map((info) => ({
          id: info.id,
          accountOwnerName: info.accountOwnerName,
          bankNumber: info.bankNumber.toString(),
          branchNumber: info.branchNumber,
          accountNumber: info.accountNumber,
          isModified: false,
        }));
      setAccounts(initialAccounts);
      setOriginalAccounts(initialAccounts);
      setError(null);
    }
  }, [open, paymentInfos]);

  // Check if an account has actual changes compared to original
  const isAccountModified = (account: EditableBankAccount): boolean => {
    // New accounts are always considered modified if they have any data
    if (account.id === null) {
      return account.accountOwnerName.trim() !== '' || account.bankNumber !== '' || account.branchNumber !== '' || account.accountNumber !== '';
    }

    // For existing accounts, compare to original
    const original = originalAccounts.find((orig) => orig.id === account.id);
    if (!original) return true;

    return (
      account.accountOwnerName !== original.accountOwnerName ||
      account.bankNumber !== original.bankNumber ||
      account.branchNumber !== original.branchNumber ||
      account.accountNumber !== original.accountNumber
    );
  };

  const handleFieldChange = (index: number, field: keyof EditableBankAccount, value: string) => {
    const updated = [...accounts];
    updated[index] = { ...updated[index], [field]: value };
    // Update isModified based on actual comparison
    updated[index].isModified = isAccountModified(updated[index]);
    setAccounts(updated);
  };

  const handleAddAccount = () => {
    setAccounts([
      ...accounts,
      {
        id: null,
        accountOwnerName: '',
        bankNumber: '',
        branchNumber: '',
        accountNumber: '',
        isModified: true,
      },
    ]);
  };

  const handleRemoveAccount = (index: number) => {
    // Only allow removing new accounts (id === null)
    if (accounts[index].id === null) {
      setAccounts(accounts.filter((_, i) => i !== index));
    }
  };

  const handleDeleteClick = (account: EditableBankAccount) => {
    setAccountToDelete(account);
  };

  const handleConfirmDelete = () => {
    if (!accountToDelete?.id) return;

    deletePaymentInfo(undefined, {
      onSuccess: () => {
        // Update local state instead of refetching to preserve unsaved changes
        const deletedId = accountToDelete.id;
        setAccounts(prev => prev.filter(acc => acc.id !== deletedId));
        setOriginalAccounts(prev => prev.filter(acc => acc.id !== deletedId));
        setAccountToDelete(null);
      },
      onError: (err) => {
        console.error('Error deleting bank account:', err);
        setError(t('supportedsManagement.editBankAccountDialog.errorDeleting'));
        setAccountToDelete(null);
      },
    });
  };

  const handleCancelDelete = () => {
    setAccountToDelete(null);
  };

  const validateAccounts = (): boolean => {
    for (const account of accounts) {
      if (!account.accountOwnerName.trim()) {
        setError(t('supportedsManagement.editBankAccountDialog.errorMissingOwnerName'));
        return false;
      }
      if (!account.bankNumber || isNaN(parseInt(account.bankNumber))) {
        setError(t('supportedsManagement.editBankAccountDialog.errorMissingBankNumber'));
        return false;
      }
      if (!account.branchNumber.trim()) {
        setError(t('supportedsManagement.editBankAccountDialog.errorMissingBranchNumber'));
        return false;
      }
      if (!account.accountNumber.trim()) {
        setError(t('supportedsManagement.editBankAccountDialog.errorMissingAccountNumber'));
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    setError(null);
    if (!validateAccounts()) return;

    const modifiedAccounts = accounts.filter((account) => account.isModified);
    if (modifiedAccounts.length === 0) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      for (const account of modifiedAccounts) {
        const payload: BankAccountPayload = {
          type: PaymentMethodType.BankAccount,
          bankNumber: parseInt(account.bankNumber),
          branchNumber: account.branchNumber,
          accountNumber: account.accountNumber,
          accountOwnerName: account.accountOwnerName,
          documents: [],
        };

        if (account.id === null) {
          // Create new account
          await createPaymentInfo(payload);
        } else {
          // Update existing account
          const headers = { Authorization: `Bearer ${currentUser?.token}` };
          await putAuth(`management/supporteds/${supported.id}/payment-infos/${account.id}`, headers, {}, payload);
        }
      }

      onSuccess();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError(t('duplicatePaymentInfoError'));
      } else {
        const errorMessage = err instanceof Error ? err.message : t('supportedsManagement.editBankAccountDialog.errorSaving');
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAccounts([]);
    setOriginalAccounts([]);
    setError(null);
    onClose();
  };

  // Check if there are any modified accounts (for enabling submit button)
  const hasModifiedAccounts = accounts.some((acc) => isAccountModified(acc));

  const renderAccountFields = (account: EditableBankAccount, index: number) => {
    const isExistingAccount = account.id !== null;
    const showCardStyle = !isExistingAccount; // All new accounts use card style

    if (showCardStyle) {
      // Card style for additional new accounts
      return (
        <div key={index} className={styles.accountCard}>
          <div className={styles.fieldsContainer}>
            <div className={styles.row}>
              <LabeledInput
                label={t('beneficiaryName')}
                id={`accountOwnerName-${index}`}
                width="414px"
                value={account.accountOwnerName}
                onChange={(e) => handleFieldChange(index, 'accountOwnerName', e.target.value)}
                placeholder={t('beneficiaryNamePlaceholder')}
                required
              />
            </div>
            <div className={styles.row}>
              <SearchableSelect
                label={t('bankCode')}
                id={`bankNumber-${index}`}
                width="262.67px"
                onChange={(value) => {
                  const bankNum = value.split(' - ')[0];
                  handleFieldChange(index, 'bankNumber', bankNum);
                }}
                value={account.bankNumber}
                loadOptions={useBanks.getBankOptions}
                placeholder="bankCodePlaceholder"
                required
              />
              <SearchableSelect
                label={t('branchNumber')}
                id={`branchNumber-${index}`}
                width="262.67px"
                onChange={(value) => {
                  const branchNum = value.split(' - ')[0];
                  handleFieldChange(index, 'branchNumber', branchNum);
                }}
                value={account.branchNumber}
                loadOptions={useBanks.getBranchOptions}
                dependency={account.bankNumber ? account.bankNumber : undefined}
                placeholder="branchNumberPlaceholder"
                dependencyPlaceholder="selectBankFirst"
                required
              />
              <LabeledInput
                label={t('accountNumber')}
                id={`accountNumber-${index}`}
                width="262.67px"
                value={account.accountNumber}
                onChange={(e) => handleFieldChange(index, 'accountNumber', e.target.value.replace(/\D/g, ''))}
                placeholder={t('accountNumberPlaceholder')}
                required
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleRemoveAccount(index)}
            className={styles.removeButton}
            title={t('supportedsManagement.editBankAccountDialog.removeBankAccount')}
          >
            <img src={minusIcon} alt="" />
          </button>
        </div>
      );
    }

    // Plain style for existing accounts and first new account when no existing
    return (
      <div key={index} className={styles.accountRow}>
        <div className={styles.fieldsContainer}>
          <div className={styles.row}>
            <LabeledInput
              label={t('beneficiaryName')}
              id={`accountOwnerName-${index}`}
              width="414px"
              value={account.accountOwnerName}
              onChange={(e) => handleFieldChange(index, 'accountOwnerName', e.target.value)}
              placeholder={t('beneficiaryNamePlaceholder')}
              required
            />
          </div>
          <div className={styles.row}>
            <SearchableSelect
              label={t('bankCode')}
              id={`bankNumber-${index}`}
              width="262.67px"
              onChange={(value) => {
                const bankNum = value.split(' - ')[0];
                handleFieldChange(index, 'bankNumber', bankNum);
              }}
              value={account.bankNumber}
              loadOptions={useBanks.getBankOptions}
              placeholder="bankCodePlaceholder"
              required
            />
            <SearchableSelect
              label={t('branchNumber')}
              id={`branchNumber-${index}`}
              width="262.67px"
              onChange={(value) => {
                const branchNum = value.split(' - ')[0];
                handleFieldChange(index, 'branchNumber', branchNum);
              }}
              value={account.branchNumber}
              loadOptions={useBanks.getBranchOptions}
              dependency={account.bankNumber ? account.bankNumber : undefined}
              placeholder="branchNumberPlaceholder"
              dependencyPlaceholder="selectBankFirst"
              required
            />
            <LabeledInput
              label={t('accountNumber')}
              id={`accountNumber-${index}`}
              width="262.67px"
              value={account.accountNumber}
              onChange={(e) => handleFieldChange(index, 'accountNumber', e.target.value.replace(/\D/g, ''))}
              placeholder={t('accountNumberPlaceholder')}
              required
            />
          </div>
        </div>
        {isExistingAccount && (
          <button
            type="button"
            onClick={() => handleDeleteClick(account)}
            className={styles.deleteButton}
            title={t('supportedsManagement.editBankAccountDialog.deleteBankAccount')}
          >
            <img src={deleteIcon} alt="" />
          </button>
        )}
      </div>
    );
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => !isOpen && handleClose()} title={t('supportedsManagement.editBankAccountDialog.title')}>
      <div className={styles.dialogContent}>
        {isLoading ? (
          <p className={styles.loading}>{t('loading')}...</p>
        ) : (
          <>
            <div className={styles.accountsContainer}>{accounts.map((account, index) => renderAccountFields(account, index))}</div>

            <button type="button" onClick={handleAddAccount} className={styles.addButton}>
              <div className={styles.addIcon}>
                <img src={addIcon} alt="" />
              </div>
              {t('supportedsManagement.editBankAccountDialog.addBankAccount')}
            </button>

            {error && <p className={styles.error}>{error}</p>}

            <div className={styles.actions}>
              <Button variant="white" onClick={handleClose} disabled={isSubmitting}>
                {t('cancel')}
              </Button>
              <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting || !hasModifiedAccounts}>
                {isSubmitting ? t('saving') : t('save')}
              </Button>
            </div>
          </>
        )}
      </div>

      <ConfirmationDialog
        open={accountToDelete !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleCancelDelete();
        }}
        title={t('supportedsManagement.editBankAccountDialog.deleteConfirmTitle')}
        message={
          linkedRequestsData?.count
            ? t('supportedsManagement.editBankAccountDialog.deleteConfirmMessageWithLinkedRequests', { count: linkedRequestsData.count })
            : t('supportedsManagement.editBankAccountDialog.deleteConfirmMessage')
        }
        confirmText="supportedsManagement.editBankAccountDialog.confirmDelete"
        cancelText="cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
        variant="danger"
      />
    </MainDialogLayout>
  );
};

export default EditBankAccountDialog;
